import React, { useMemo, useState, useEffect } from 'react';
import { SessionStepProps, SessionStaticProps } from './SessionFlowBase';
import { SyncView } from '@balljames/videosync';
import { TSync } from '@balljames/videosync/build/components/SyncView/SyncView';
import styled from 'styled-components';
import { TSession } from './Sessions';
import useSession, { refreshSession } from '../Util/useSession';
import sessionsService from '../../services/sessions';
import { Video, secondsToTimeSpan, timeSpanToSeconds } from '../../services/sessionModels';

const syncOffsetMs = (video: Video) => Math.round(timeSpanToSeconds(video.syncOffset) * 1000);

interface Props extends SessionStepProps {}

export const syncValue = (syncValues: Array<TSync>, url: string | null | undefined) => {
  for (const syncV of syncValues) {
    if (syncV.url === url && syncV.offset) {
      return syncV.offset;
    }
  }
  return 0;
};

const SessionVideosync: React.FunctionComponent<Props> & SessionStaticProps = ({ sessionid, setNextEnabled }) => {
  const { session } = useSession(sessionid);
  const [sync, setSync] = useState<Array<TSync> | null>();

  const videos: Array<TSync> = useMemo(() => {
    return (
      session?.videos
        ?.slice()
        ?.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        ?.map((v) => {
          return { url: v.url || '', offset: syncOffsetMs(v), name: 'Video ' + v.sortOrder };
        }) || []
    );
  }, [session]);

  const framerate = useMemo(() => {
    return (
      session?.videos
        ?.slice()
        ?.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        ?.map((v) => v.fps)
        ?.find((fps) => fps && fps > 0) || 25
    );
  }, [session]);

  useEffect(() => {
    const newSync = sync && JSON.stringify(sync);

    if (newSync) {
      setNextEnabled(true);
    } else if (!sync) {
      setNextEnabled(false);
    }
    const updates = (sync || [])
      .map((s) => ({ s, video: session?.videos?.find((v) => v.url === s.url) }))
      .filter(({ s, video }) => video && video.url && (s.offset || 0) !== syncOffsetMs(video));
    if (updates.length === 0) return;
    Promise.all(
      updates.map(({ s, video }) =>
        sessionsService.updateVideo(video!.id, {
          url: video!.url,
          sortOrder: video!.sortOrder || 0,
          calibration: video!.calibration || '',
          clickPoints: video!.clickPoints,
          syncOffset: secondsToTimeSpan((s.offset || 0) / 1000),
        })
      )
    ).then(() => refreshSession(sessionid));
  }, [sessionid, session, setNextEnabled, sync]);

  return (
    <Wrapper>
      <SyncView
        framerate={framerate}
        baseUrl={process.env.REACT_APP_PORTAL_URL + '/gateway/videosync'}
        apikey="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJiYWxsamFtZXMiLCJhY2NvdW50IjoiYmFsbGphbWVzLWVuZ2luZSJ9.s8V54DAY5vBPEfsQBTxpPepKtkbkVrwZP6CL5eWR1Kg"
        videos={videos}
        onUpdate={setSync}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  height: calc(100vh - 350px);
`;

SessionVideosync.isComplete = (session: TSession) => {
  for (let video of session?.videos || []) {
    if (video.syncOffset === undefined || video.syncOffset === null) {
      return false;
    }
  }
  return true;
};
export default SessionVideosync;
